// ============================================
// CENTRALIZED KEYBOARD SHORTCUTS
// ============================================
// All DAW keyboard shortcuts are defined here so they can be reused,
// displayed in a help panel, and reassigned in the future.
//
// Each shortcut has:
//   id       – unique identifier used in handler lookup
//   key      – the e.key value (case-insensitive for letters)
//   mod      – requires Cmd (Mac) / Ctrl (Win)
//   shift    – requires Shift
//   alt      – requires Alt / Option
//   label    – human-readable label
//   category – grouping for UI display

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
export const MOD_KEY_LABEL = IS_MAC ? '⌘' : 'Ctrl';

export const SHORTCUTS = {
  // ── Global / Transport ──────────────────────
  UNDO: { id: 'UNDO', key: 'z', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+Z`, category: 'General' },
  REDO: { id: 'REDO', key: 'z', mod: true, shift: true, alt: false, label: `${MOD_KEY_LABEL}+Shift+Z`, category: 'General' },
  REDO_ALT: { id: 'REDO_ALT', key: 'y', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+Y`, category: 'General' },
  DELETE: { id: 'DELETE', key: 'Delete', mod: false, shift: false, alt: false, label: 'Delete', category: 'General' },
  DELETE_BACKSPACE: { id: 'DELETE_BACKSPACE', key: 'Backspace', mod: false, shift: false, alt: false, label: 'Backspace', category: 'General' },
  COPY: { id: 'COPY', key: 'c', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+C`, category: 'General' },
  PASTE: { id: 'PASTE', key: 'v', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+V`, category: 'General' },
  CUT: { id: 'CUT', key: 'x', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+X`, category: 'General' },
  SELECT_ALL: { id: 'SELECT_ALL', key: 'a', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+A`, category: 'General' },
  DUPLICATE: { id: 'DUPLICATE', key: 'd', mod: true, shift: false, alt: false, label: `${MOD_KEY_LABEL}+D`, category: 'General' },

  // ── Piano Roll ──────────────────────────────
  TRANSPOSE_UP: { id: 'TRANSPOSE_UP', key: 'ArrowUp', mod: false, shift: false, alt: true, label: 'Alt+↑', category: 'Piano Roll' },
  TRANSPOSE_DOWN: { id: 'TRANSPOSE_DOWN', key: 'ArrowDown', mod: false, shift: false, alt: true, label: 'Alt+↓', category: 'Piano Roll' },
  OCTAVE_UP: { id: 'OCTAVE_UP', key: 'ArrowUp', mod: false, shift: true, alt: false, label: 'Shift+↑', category: 'Piano Roll' },
  OCTAVE_DOWN: { id: 'OCTAVE_DOWN', key: 'ArrowDown', mod: false, shift: true, alt: false, label: 'Shift+↓', category: 'Piano Roll' },
};

/**
 * Check if a keyboard event matches a shortcut definition.
 * @param {KeyboardEvent} e
 * @param {object} shortcut – one of the SHORTCUTS entries
 * @returns {boolean}
 */
export function matchesShortcut(e, shortcut) {
  const mod = e.metaKey || e.ctrlKey;
  if (shortcut.mod && !mod) return false;
  if (!shortcut.mod && mod) return false;
  if (shortcut.shift && !e.shiftKey) return false;
  if (!shortcut.shift && e.shiftKey) return false;
  if (shortcut.alt && !e.altKey) return false;
  if (!shortcut.alt && e.altKey) return false;
  return e.key.toLowerCase() === shortcut.key.toLowerCase();
}

/**
 * Get all shortcuts in a given category.
 */
export function getShortcutsByCategory(category) {
  return Object.values(SHORTCUTS).filter(s => s.category === category);
}
